import { forwardRef } from 'react';
import { calcTotalStoneWeight, DEFAULT_ANNOTATION_STYLE, DEFAULT_TEXTBOX_STYLE } from '@/utils/cad-sheet';

const DEFAULT_VISIBILITY = {
  header: true,
  images: true,
  stones: true,
  findings: true,
  finishing: true,
  engraving: true,
  customerImages: true,
  notes: true,
};

function HtmlLine({ x1, y1, x2, y2, color = '#000', width = 2, dashed = false, opacity = 1 }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < 0.1) return null;
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  return (
    <div style={{
      position: 'absolute',
      left: `${x1}%`,
      top: `${y1}%`,
      width: `${length}%`,
      height: 0,
      borderTop: `${width}px ${dashed ? 'dashed' : 'solid'} ${color}`,
      transformOrigin: '0 0',
      transform: `rotate(${angle}deg)`,
      opacity,
      pointerEvents: 'none',
      zIndex: 5,
    }} />
  );
}

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

export const CADSheetBody = forwardRef(function CADSheetBody(
  { data, visibility = DEFAULT_VISIBILITY, showCompanyFooter = true, withShadow = false },
  ref,
) {
  const totalStoneWeight = calcTotalStoneWeight(data.stones);
  const totalStoneQty = data.stones.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);

  const pageStyle = {
    width: '800px',
    height: '1130px',
    minWidth: '800px',
    minHeight: '1130px',
    maxWidth: '800px',
    maxHeight: '1130px',
    padding: '0',
    backgroundColor: '#ffffff',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#000000',
    border: '2px solid #000',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxSizing: 'border-box',
    flexShrink: 0,
    flexGrow: 0,
    ...(withShadow ? { boxShadow: '0 4px 24px rgba(0,0,0,0.12)' } : {}),
  };

  const page1ImagesCount = 2;

  const allImages = data.images || [];
  const imageChunks = [];

  if (allImages.length > 0) {
    if (page1ImagesCount > 0) {
      imageChunks.push(allImages.slice(0, page1ImagesCount));
      const remaining = allImages.slice(page1ImagesCount);
      for (let i = 0; i < remaining.length; i += 4) {
        imageChunks.push(remaining.slice(i, i + 4));
      }
    } else {
      imageChunks.push([]); // Page 1 has 0 CAD images
      for (let i = 0; i < allImages.length; i += 4) {
        imageChunks.push(allImages.slice(i, i + 4));
      }
    }
  } else {
    imageChunks.push([]);
  }

  const pagesCount = imageChunks.length;

  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
      <style>{`
        @media print {
          .cad-sheet-page-print {
            page-break-after: always !important;
            break-after: page !important;
            box-shadow: none !important;
            margin: 0 !important;
            border: 2px solid #000 !important;
          }
        }
      `}</style>
      
      {Array.from({ length: pagesCount }).map((_, pageIdx) => {
        const isFirstPage = pageIdx === 0;
        const currentImages = imageChunks[pageIdx] || [];

        return (
          <div key={pageIdx} style={pageStyle} className="cad-sheet-page-print">
            {visibility.header && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 16px', borderBottom: '2px solid #000' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#000' }}>
                    Style No: {data.styleNo || '—'} {!isFirstPage && `(Page ${pageIdx + 1})`}
                  </div>
                  <div style={{ fontSize: '12px', color: '#444', marginTop: '2px' }}>{data.jewelryType}{data.size ? ` | Size: ${data.size}` : ''}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {data.customerDesignNo && <div style={{ fontSize: '13px', fontWeight: 700, color: '#000' }}>Customer No: {data.customerDesignNo}</div>}
                  <div style={{ fontSize: '12px', color: '#444' }}>Date: {data.date}</div>
                  {data.designerName && <div style={{ fontSize: '13px', fontWeight: 700, color: '#000' }}>#{data.designerName.replace(/[^a-zA-Z]/g, '').slice(0, 3)}</div>}
                </div>
              </div>
            )}

            {isFirstPage && (
              <div style={{ display: 'flex', borderBottom: '2px solid #000', padding: '12px 16px', gap: '16px' }}>
                {/* Left Column: Metal, Findings, finishing, engraving, notes */}
                <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '4px' }}>Metal & Weight</div>
                    <div style={{ color: '#333' }}>{data.metals.map(m => `${m.karat} ${m.color}${m.netWeight ? ` (${m.netWeight}g)` : ''}`).join(' / ')}</div>
                    {(data.netWeight || data.grossWeight) && <div style={{ color: '#555', marginTop: '2px' }}>{data.netWeight && `Net: ${data.netWeight}g`}{data.netWeight && data.grossWeight && ' | '}{data.grossWeight && `Gross: ${data.grossWeight}g`}</div>}
                  </div>

                  {visibility.findings && data.findings.length > 0 && (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '4px' }}>Findings</div>
                      {data.findings.filter(f => f.type).map((f) => (<div key={f.id} style={{ color: '#333', fontSize: '11px' }}>{f.type}{f.weight && ` — ${f.weight}g`}{f.details && ` (${f.details})`}</div>))}
                    </div>
                  )}

                  {visibility.finishing && data.finishing && (
                    <div style={{ marginTop: '2px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>Finishing: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.finishing}</span></div>
                  )}

                  {visibility.engraving && (data.engravingInstructions || data.engravingImage?.url || data.stampingInstructions || (data.stampingImages && data.stampingImages.length > 0)) && (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '4px' }}>Engraving & Stamping</div>
                      {data.engravingInstructions && <div style={{ marginBottom: '4px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>Engraving: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.engravingInstructions}</span></div>}
                      {data.engravingImage?.url && <div style={{ marginBottom: '4px' }}><img src={data.engravingImage.url} alt="Engraving ref" crossOrigin="anonymous" style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', border: '1px solid #ccc' }} /></div>}
                      {data.stampingInstructions && <div style={{ marginBottom: '4px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>Stamping: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.stampingInstructions}</span></div>}
                      {data.stampingImages && data.stampingImages.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {data.stampingImages.map((img, i) => img.url && (
                            <img key={i} src={img.url} alt={`Stamping ref ${i + 1}`} crossOrigin="anonymous" style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', border: '1px solid #ccc' }} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {visibility.notes && (data.otherDetails || data.ksNotes) && (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '4px' }}>Notes</div>
                      {data.otherDetails && <div style={{ marginBottom: '2px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>Notes: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.otherDetails}</span></div>}
                      {data.ksNotes && <div style={{ marginBottom: '2px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>KS Notes: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.ksNotes}</span></div>}
                    </div>
                  )}
                </div>

                {/* Right Column: Stones Table */}
                <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                  {visibility.stones && data.stones.length > 0 ? (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '4px' }}>Stones <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>({totalStoneQty} pcs, {totalStoneWeight.toFixed(2)} ct)</span></div>
                      <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ borderBottom: '1px solid #999' }}>{['Stone', 'Own.', 'Cut', 'Cat.', 'Qlty', 'Size', 'Ct.Wt', 'Qty', 'Tot'].map(h => (<th key={h} style={{ textAlign: 'left', padding: '2px 3px', fontSize: '9px', fontWeight: 700 }}>{h}</th>))}</tr></thead>
                        <tbody>{data.stones.map(s => (<tr key={s.id} style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '2px 3px' }}>{s.stone_category || '—'}</td><td style={{ padding: '2px 3px' }}>{s.ownership || '—'}</td><td style={{ padding: '2px 3px' }}>{s.stone_shape || s.type || '—'}</td><td style={{ padding: '2px 3px' }}>{s.category ? s.category.replace(' Stone','')[0] : '—'}</td><td style={{ padding: '2px 3px' }}>{s.stone_quality || s.quality || '—'}</td><td style={{ padding: '2px 3px' }}>{s.size_mm || s.size || '—'}</td><td style={{ padding: '2px 3px' }}>{s.weight_per_stone || s.weight || '—'}</td><td style={{ padding: '2px 3px' }}>{s.quantity || '—'}</td><td style={{ padding: '2px 3px' }}>{((parseFloat(s.weight_per_stone ?? s.weight) || 0) * (parseInt(s.quantity) || 0)).toFixed(2)}</td></tr>))}</tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', fontSize: '12px' }}>
                      No Stones
                    </div>
                  )}
                  
                  {visibility.customerImages && data.customerImages.length > 0 && (
                    <div style={{ marginTop: 'auto' }}>
                      <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>Customer Reference</div>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{data.customerImages.map((img, i) => (<img key={i} src={img.url} alt={`Ref ${i + 1}`} crossOrigin="anonymous" style={{ maxWidth: '80px', maxHeight: '60px', objectFit: 'contain', border: '1px solid #ccc' }} />))}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CAD Images Grid */}
            {visibility.images && currentImages.length > 0 && (
              <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {currentImages.map((img, i) => (
                    <div key={i} style={{ position: 'relative', border: '1px solid #ccc', aspectRatio: '1', backgroundColor: '#f9f9f9', overflow: 'hidden' }}>
                      {img.url ? (
                        <>
                          <img src={img.url} alt={img.label} crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          {(img.highlights || []).length > 0 && (
                            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }} viewBox="0 0 100 100" preserveAspectRatio="none">
                              {(img.highlights || []).map(hl => (<path key={hl.id} d={hl.points.map((p, j) => `${j === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')} fill="none" stroke={hl.color} strokeWidth={hl.strokeWidth} strokeLinecap="round" strokeLinejoin="round" opacity={hl.opacity} />))}
                            </svg>
                          )}
                          {img.annotations.map(ann => {
                            const style = ann.style || DEFAULT_ANNOTATION_STYLE;
                            const dg = ann.dimensionGeometry;
                            const lc = style.lineColor || '#000';
                            const textX = (ann.textOffsetX !== undefined) ? (ann.x1+ann.x2)/2+ann.textOffsetX : (ann.x1+ann.x2)/2;
                            const textY = (ann.textOffsetY !== undefined) ? (ann.y1+ann.y2)/2+ann.textOffsetY : (ann.y1+ann.y2)/2-1.5;
                            const refX = dg ? (dg.dimStart.x+dg.dimEnd.x)/2 : (ann.x1+ann.x2)/2;
                            const refY = dg ? (dg.dimStart.y+dg.dimEnd.y)/2 : (ann.y1+ann.y2)/2;
                            const lineEls = [];
                            if (dg) {
                              lineEls.push(<HtmlLine key={`ext1-${ann.id}`} x1={dg.ext1Start.x} y1={dg.ext1Start.y} x2={dg.ext1End.x} y2={dg.ext1End.y} color={lc} width={2} dashed opacity={0.85} />);
                              lineEls.push(<HtmlLine key={`ext2-${ann.id}`} x1={dg.ext2Start.x} y1={dg.ext2Start.y} x2={dg.ext2End.x} y2={dg.ext2End.y} color={lc} width={2} dashed opacity={0.85} />);
                              lineEls.push(<HtmlLine key={`dim-${ann.id}`} x1={dg.dimStart.x} y1={dg.dimStart.y} x2={dg.dimEnd.x} y2={dg.dimEnd.y} color={lc} width={2} />);
                              const dx=dg.dimEnd.x-dg.dimStart.x, dy=dg.dimEnd.y-dg.dimStart.y, len=Math.sqrt(dx*dx+dy*dy);
                              if (len > 0.5) { const nx=-dy/len*2.5, ny=dx/len*2.5;
                                lineEls.push(<HtmlLine key={`t1-${ann.id}`} x1={dg.dimStart.x-nx} y1={dg.dimStart.y-ny} x2={dg.dimStart.x+nx} y2={dg.dimStart.y+ny} color={lc} width={2} />);
                                lineEls.push(<HtmlLine key={`t2-${ann.id}`} x1={dg.dimEnd.x-nx} y1={dg.dimEnd.y-ny} x2={dg.dimEnd.x+nx} y2={dg.dimEnd.y+ny} color={lc} width={2} />);
                              }
                            } else {
                              lineEls.push(<HtmlLine key={`line-${ann.id}`} x1={ann.x1} y1={ann.y1} x2={ann.x2} y2={ann.y2} color={lc} width={2} />);
                              const dx=ann.x2-ann.x1, dy=ann.y2-ann.y1, len=Math.sqrt(dx*dx+dy*dy);
                              if (len > 0.5) { const nx=-dy/len*2.5, ny=dx/len*2.5;
                                lineEls.push(<HtmlLine key={`t1-${ann.id}`} x1={ann.x1-nx} y1={ann.y1-ny} x2={ann.x1+nx} y2={ann.y1+ny} color={lc} width={2} />);
                                lineEls.push(<HtmlLine key={`t2-${ann.id}`} x1={ann.x2-nx} y1={ann.y2-ny} x2={ann.x2+nx} y2={ann.y2+ny} color={lc} width={2} />);
                              }
                            }
                            if (Math.abs(textX-refX) > 1 || Math.abs(textY-refY) > 1) {
                              lineEls.push(<HtmlLine key={`leader-${ann.id}`} x1={refX} y1={refY} x2={textX} y2={textY} color={lc} width={1.5} dashed opacity={0.7} />);
                            }
                            return <div key={`lines-${ann.id}`}>{lineEls}</div>;
                          })}
                          {img.annotations.map(ann => {
                            const style = ann.style || DEFAULT_ANNOTATION_STYLE;
                            const textX = (ann.textOffsetX !== undefined) ? (ann.x1+ann.x2)/2+ann.textOffsetX : (ann.x1+ann.x2)/2;
                            const textY = (ann.textOffsetY !== undefined) ? (ann.y1+ann.y2)/2+ann.textOffsetY : (ann.y1+ann.y2)/2-1.5;
                            return (
                              <div key={`pv-text-${ann.id}`} style={{ position: 'absolute', left: `${textX}%`, top: `${textY}%`, transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 10 }}>
                                <span style={{ display: 'inline-block', padding: '1px 3px', borderRadius: '2px', whiteSpace: 'nowrap', fontSize: `clamp(7px, ${(style.fontSize || 4.5) * 0.2}rem, 14px)`, fontWeight: 'bold', fontFamily: 'Arial, sans-serif', color: style.textColor || '#000', backgroundColor: 'rgba(255,255,255,0.9)', border: '1px solid rgba(0,0,0,0.15)', lineHeight: 1.2 }}>{ann.value}</span>
                              </div>
                            );
                          })}
                          {(img.textBoxes || []).map(tb => {
                            const style = tb.style || DEFAULT_TEXTBOX_STYLE;
                            return (
                              <div key={`pv-tb-${tb.id}`} style={{ position: 'absolute', left: `${tb.x}%`, top: `${tb.y}%`, transform: 'translate(0, -50%)', pointerEvents: 'none', zIndex: 10 }}>
                                <span style={{ display: 'inline-block', whiteSpace: 'nowrap', fontSize: `clamp(7px, ${(style.fontSize || 4.5) * 0.2}rem, 14px)`, fontWeight: style.bold ? 'bold' : 'normal', fontFamily: 'Arial, sans-serif', color: style.color || '#000', lineHeight: 1.2 }}>{tb.value}</span>
                              </div>
                            );
                          })}
                        </>
                      ) : (<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#aaa' }}>{img.label}</div>)}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1px 4px', fontSize: '9px', fontWeight: 600, backgroundColor: 'rgba(255,255,255,0.85)', color: '#333', textAlign: 'center' }}>{img.label}</div>
                    </div>
                  ))}
                </div>
                {isFirstPage && (
                  <div style={{ fontSize: '9px', color: '#888', fontStyle: 'italic', marginTop: '6px' }}>* Net & gross weights do not include suggested findings weight.</div>
                )}
              </div>
            )}

            {showCompanyFooter && (
              <div style={{ position: 'absolute', bottom: '8px', left: 0, right: 0, padding: '6px 16px', textAlign: 'center', fontSize: '10px', color: '#999', borderTop: '1px solid #eee' }}>
                K's Jewelry &mdash; CAD Sheet &mdash; Page {pageIdx + 1} of {pagesCount}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

export { DEFAULT_VISIBILITY };
